import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { POST_STATUS, type PostPatchInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const I18n = z.record(z.string());

const PostRow = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(POST_STATUS),
  title: I18n.nullable(),
  excerpt: I18n.nullable(),
  cover_url: z.string().nullable(),
  category: z.string().nullable(),
  author: z.string().nullable(),
  published_at: z.string().nullable(),
  updated_at: z.string(),
});
export type PostRow = z.infer<typeof PostRow>;

const PostDetail = PostRow.extend({
  body: I18n.nullable(),
  tags: z.array(z.string()).nullable(),
  created_at: z.string().optional(),
});
export type PostDetail = z.infer<typeof PostDetail>;

export function usePosts() {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["posts"],
    enabled: !!token,
    queryFn: async (): Promise<PostRow[]> => {
      const r = await apiFetch("/api/v1/admin/posts", token!, z.object({ items: z.array(PostRow) }));
      return r.items;
    },
  });
}

export function usePost(id: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["post", id],
    enabled: !!token && !!id,
    queryFn: () => apiFetch(`/api/v1/admin/posts/${id!}`, token!, PostDetail),
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: (body: { slug: string; title?: Record<string, string> }) =>
      apiFetch("/api/v1/admin/posts", token!, z.object({ id: z.string().uuid(), slug: z.string() }), { method: "POST", body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["posts"] }); },
  });
}

export function usePatchPost(id: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: (patch: PostPatchInput) =>
      apiFetch(`/api/v1/admin/posts/${id}`, token!, z.object({ id: z.string(), status: z.string() }), { method: "PATCH", body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["post", id] });
      void qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
}
